import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Building2, Users, Calendar as CalendarIcon, AlertTriangle, Pencil, MapPin, CheckCircle, CheckCircle2, Loader2, XCircle, Check, ChevronsUpDown, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useQuoteWizardContext } from "../QuoteWizardContext";
import { getSicCodeDisplay } from "../constants";
import { EditEntityFieldsDialog } from "../components/EditEntityFieldsDialog";
import { useResolveGeo } from "@/hooks/useQuoteValidation";
import { useQuery } from "@tanstack/react-query";

// Shape returned by GET /api/rating-areas?state= (geo-backed).
type RatingArea = { id: number; areaNumber: number; label: string; state: string; description?: string | null };

export function Step1SelectEntity() {
  const { quoteData, updateQuoteData, clients, prospects, getEntityValidation, isRFPMode } = useQuoteWizardContext();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [userManuallyConfirmed, setUserManuallyConfirmed] = useState(false);
  const [entityPickerOpen, setEntityPickerOpen] = useState(false);

  // Alphabetized list of clients or prospects for the picker.
  const entityOptions = useMemo(() => {
    const list = quoteData.clientType === "client" ? clients : prospects;
    return [...(list || [])].sort((a: any, b: any) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), undefined, { sensitivity: "base" })
    );
  }, [quoteData.clientType, clients, prospects]);

  // Get validation status for the selected entity
  const entityValidation = getEntityValidation();

  // Get entity state for rate area lookups
  const entityState = quoteData.selectedEntity?.state?.toUpperCase() || "";
  const entityZip = quoteData.selectedEntity?.zipCode;

  // Fetch rating areas for the entity's state from database
  // A failed request is an error (shown with Retry), NOT an empty list - an
  // empty list means "not configured", which a 5xx isn't (DEV-382 QA #5).
  const {
    data: ratingAreas = [],
    isLoading: isLoadingRatingAreas,
    isError: isRatingAreasError,
    isFetching: isFetchingRatingAreas,
    refetch: refetchRatingAreas,
  } = useQuery<RatingArea[]>({
    queryKey: ["/api/rating-areas", entityState],
    queryFn: async () => {
      if (!entityState) return [];
      const response = await fetch(`/api/rating-areas?state=${entityState}`);
      if (!response.ok) throw new Error(`Rate areas request failed (${response.status})`);
      return response.json();
    },
    enabled: !!entityState && !isRFPMode,
    retry: 1,
  });

  // Check if quoting is available for this state (has rating areas in database)
  const isQuotingAvailable = ratingAreas.length > 0;

  // Resolve geo context for rating area display (only for Quote mode, not RFP)
  const {
    data: geoContext,
    isLoading: isLoadingGeo,
    isError: isGeoError,
    refetch: refetchGeo,
  } = useResolveGeo(
    entityState,
    isRFPMode ? undefined : entityZip, // Skip geo resolution in RFP mode
    quoteData.effectiveDate || undefined,
    quoteData.selectedEntity?.county || undefined, // resolves county-based states
  );

  // Track previous entity ID/ZIP/State to detect entity changes
  const prevEntityKeyRef = useRef<string | null>(null);
  const prevEntityStateRef = useRef<string | null>(null);

  // Reset userManuallyConfirmed and clear confirmedRatingArea when entity or state changes
  useEffect(() => {
    const currentEntityKey = quoteData.selectedEntity?.id 
      ? `${quoteData.selectedEntity.id}-${quoteData.selectedEntity.zipCode || ''}`
      : null;
    const currentState = quoteData.selectedEntity?.state?.toUpperCase() || null;
    
    if (prevEntityKeyRef.current !== null && prevEntityKeyRef.current !== currentEntityKey) {
      // Entity changed, reset manual confirmation so auto-sync resumes
      setUserManuallyConfirmed(false);
    }
    
    // Clear confirmedRatingArea when state changes (different rating areas for each state)
    if (prevEntityStateRef.current !== null && prevEntityStateRef.current !== currentState) {
      updateQuoteData({ confirmedRatingArea: null });
      setUserManuallyConfirmed(false);
    }
    
    prevEntityKeyRef.current = currentEntityKey;
    prevEntityStateRef.current = currentState;
  }, [quoteData.selectedEntity?.id, quoteData.selectedEntity?.zipCode, quoteData.selectedEntity?.state]);

  // Auto-set confirmed rating area when geoContext loads or changes
  // Only auto-update if user hasn't manually confirmed a different area
  useEffect(() => {
    if (geoContext?.ratingAreaId && ratingAreas.length > 0) {
      const areaNum = parseInt(geoContext.ratingAreaId.replace(/\D/g, ''), 10);
      const matchedArea = ratingAreas.find((a) => a.areaNumber === areaNum);
      if (matchedArea) {
        // Auto-set if no confirmed area, or if user hasn't manually confirmed
        if (!quoteData.confirmedRatingArea || !userManuallyConfirmed) {
          updateQuoteData({
            confirmedRatingArea: {
              id: matchedArea.areaNumber,
              label: matchedArea.label,
            },
          });
        }
      }
    }
  }, [geoContext?.ratingAreaId, userManuallyConfirmed, ratingAreas]);

  // Handle successful update from dialog
  const handleEntityUpdate = (updatedEntity: any) => {
    // Update the selected entity in quote data with fresh values
    updateQuoteData({ selectedEntity: updatedEntity });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Select Client or Prospect</h3>
        <p className="text-muted-foreground">
          Choose who you want to {isRFPMode ? "send this RFP to" : "generate a quote for"}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(
          [
            {
              type: "client" as const,
              icon: Building2,
              title: "Existing Client",
              description: isRFPMode ? "Send an RFP for an existing client" : "Quote for an existing client",
            },
            {
              type: "prospect" as const,
              icon: Users,
              title: "Prospect",
              description: isRFPMode ? "Send an RFP for a potential client" : "Quote for a potential client",
            },
          ]
        ).map(({ type, icon: Icon, title, description }) => {
          const isSelected = quoteData.clientType === type;
          return (
            <div
              key={type}
              className={cn(
                "cursor-pointer rounded-lg border p-6 text-center transition-colors",
                isSelected ? "border-primary/40 bg-primary/5" : "hover:bg-muted/40"
              )}
              onClick={() => updateQuoteData({ clientType: type, selectedEntity: null })}
            >
              <span
                className={cn(
                  "mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl",
                  isSelected ? "bg-primary/10" : "bg-muted"
                )}
              >
                <Icon className={cn("h-6 w-6", isSelected ? "text-primary" : "text-muted-foreground")} />
              </span>
              <p className="flex items-center justify-center gap-1.5 font-medium">
                {title}
                {isSelected && <CheckCircle className="h-4 w-4 text-primary" />}
              </p>
              <p className="text-sm text-muted-foreground mt-1">{description}</p>
            </div>
          );
        })}
      </div>

      {quoteData.clientType && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Select {quoteData.clientType === "client" ? "Client" : "Prospect"}
            </label>
            <Popover open={entityPickerOpen} onOpenChange={setEntityPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={entityPickerOpen}
                  className={cn(
                    "w-full justify-between font-normal",
                    !quoteData.selectedEntity && "text-muted-foreground"
                  )}
                >
                  {quoteData.selectedEntity?.name || `Choose a ${quoteData.clientType}`}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[--radix-popover-trigger-width] p-0"
                align="start"
              >
                <Command
                  filter={(value, search) =>
                    value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
                  }
                >
                  <CommandInput placeholder={`Search ${quoteData.clientType}s...`} />
                  <CommandList>
                    <CommandEmpty>No {quoteData.clientType} found.</CommandEmpty>
                    <CommandGroup>
                      {entityOptions.map((entity: any) => (
                        <CommandItem
                          key={entity.id}
                          value={entity.name}
                          onSelect={() => {
                            updateQuoteData({ selectedEntity: entity });
                            setEntityPickerOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              quoteData.selectedEntity?.id === entity.id
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                          {entity.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {quoteData.selectedEntity && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Zip Code</label>
                  <Input
                    value={quoteData.selectedEntity.zipCode || ""}
                    readOnly
                    className={cn(
                      "bg-muted",
                      !quoteData.selectedEntity.zipCode && "border-amber-500"
                    )}
                    placeholder="Not set"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">SIC Code</label>
                  <Input
                    value={getSicCodeDisplay(
                      quoteData.selectedEntity.sicCodeId,
                      quoteData.selectedEntity.sic
                    )}
                    readOnly
                    className={cn(
                      "bg-muted",
                      !quoteData.selectedEntity.sicCodeId && !quoteData.selectedEntity.sic && "border-amber-500"
                    )}
                    placeholder="Not set"
                  />
                </div>
              </div>

              {/* Geographic Context / Rating Area Display - Only for Quote mode */}
              {!isRFPMode && quoteData.selectedEntity.zipCode && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Detected Area:</span>
                  {isLoadingGeo ? (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span className="text-sm">Resolving...</span>
                    </div>
                  ) : geoContext?.ratingAreaId ? (
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant="outline" 
                        className={cn(
                          geoContext.isExactMatch 
                            ? "bg-green-50 border-green-300 text-green-700" 
                            : "bg-amber-50 border-amber-300 text-amber-700"
                        )}
                      >
                        {geoContext.ratingAreaName || geoContext.ratingAreaId}
                      </Badge>
                      {geoContext.isExactMatch && (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      )}
                      {!geoContext.isExactMatch && (
                        <span className="text-xs text-muted-foreground">(approximate)</span>
                      )}
                    </div>
                  ) : isGeoError ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-red-700" data-testid="text-geo-error">
                        Couldn't resolve the rate area.
                      </span>
                      <Button variant="outline" size="sm" onClick={() => refetchGeo()} data-testid="button-retry-geo">
                        <RefreshCw className="h-3 w-3 mr-1" /> Retry
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-muted-foreground">
                        Default Rating
                      </Badge>
                      {geoContext?.warnings?.[0] && (
                        <span className="text-xs text-amber-700" data-testid="text-geo-warning">
                          {geoContext.warnings[0]}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Rate areas request failed (5xx/network) - error + retry, not "not configured" */}
              {!isRFPMode && entityState && !isLoadingRatingAreas && isRatingAreasError && !isQuotingAvailable && (
                <Alert className="bg-red-50 border-red-200" data-testid="alert-rating-areas-error">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    <span className="font-medium">Couldn't load rate areas for {entityState}</span>
                    <p className="text-sm mt-1 text-red-600">
                      The server didn't respond correctly. This is usually temporary - try again.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      disabled={isFetchingRatingAreas}
                      onClick={() => {
                        refetchRatingAreas();
                        if (isGeoError) refetchGeo();
                      }}
                      data-testid="button-retry-rating-areas"
                    >
                      {isFetchingRatingAreas ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3 w-3 mr-1" />
                      )}
                      Retry
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {/* Quoting Not Available Warning - Show when state has no rating areas */}
              {!isRFPMode && entityState && !isLoadingRatingAreas && !isRatingAreasError && !isQuotingAvailable && (
                <Alert className="bg-red-50 border-red-200">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    <span className="font-medium">Quoting Not Yet Available For {entityState}</span>
                    <p className="text-sm mt-1 text-red-600">
                      Rate areas for this state have not been configured. Please contact your administrator.
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {/* Confirm Rate Area Selection - Only for Quote mode when state is supported */}
              {!isRFPMode && isQuotingAvailable && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Confirm Rate Area
                    <span className="text-xs text-muted-foreground ml-2">(used for rate lookups)</span>
                  </label>
                  {isLoadingRatingAreas ? (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm text-muted-foreground">Loading rate areas...</span>
                    </div>
                  ) : (
                    <Select
                      value={quoteData.confirmedRatingArea?.id?.toString() || ""}
                      onValueChange={(value) => {
                        const areaNum = parseInt(value, 10);
                        const areaInfo = ratingAreas.find((a) => a.areaNumber === areaNum);
                        if (areaInfo) {
                          setUserManuallyConfirmed(true);
                          updateQuoteData({
                            confirmedRatingArea: {
                              id: areaInfo.areaNumber,
                              label: areaInfo.label,
                            },
                          });
                        }
                      }}
                    >
                      <SelectTrigger className={cn(
                        !quoteData.confirmedRatingArea && "border-amber-500"
                      )}>
                        <SelectValue placeholder="Select rating area" />
                      </SelectTrigger>
                      <SelectContent>
                        {ratingAreas
                          .sort((a, b) => a.areaNumber - b.areaNumber)
                          .map((area) => (
                            <SelectItem key={area.id} value={area.areaNumber.toString()}>
                              {area.label}
                              {area.description && ` - ${area.description.slice(0, 40)}${area.description.length > 40 ? '...' : ''}`}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  )}
                  {quoteData.confirmedRatingArea && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Rates will be pulled for {quoteData.confirmedRatingArea.label}
                    </p>
                  )}
                </div>
              )}

              {/* Validation Warning */}
              {!entityValidation.isValid && (
                <Alert variant="destructive" className="bg-amber-50 border-amber-200 text-amber-800">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="flex items-center justify-between gap-4">
                    <span>
                      <span className="font-medium">Missing required information:</span>{" "}
                      {entityValidation.missingFields.join(" and ")}.
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0 bg-white hover:bg-amber-100 border-amber-300 hover:text-amber-900"
                      onClick={() => setEditDialogOpen(true)}
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Add Now
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {/* Edit Entity Fields Dialog */}
              <EditEntityFieldsDialog
                open={editDialogOpen}
                onOpenChange={setEditDialogOpen}
                entity={quoteData.selectedEntity}
                entityType={quoteData.clientType as "client" | "prospect"}
                missingFields={entityValidation.missingFields}
                onSuccess={handleEntityUpdate}
              />
            </>
          )}

          <div>
            <label className="block text-sm font-medium mb-2">Effective Date</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !quoteData.effectiveDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {quoteData.effectiveDate
                    ? format(quoteData.effectiveDate, "PPP")
                    : "Select effective date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={quoteData.effectiveDate || undefined}
                  onSelect={(date) => updateQuoteData({ effectiveDate: date || null })}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      )}
    </div>
  );
}
